import type { CaptureOptions, PostHog } from 'posthog-js';
import { ATTRIBUTION_PARAMS, PostHogManager } from '#~/posthog/index.js';
import { config } from '#~/config/config.js';
import { isElectronApp } from '#~/electron/utils.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { COMMON_PROPERTIES, ENVELOPE_CLIENT } from '#~/analytics/constants/index.js';
import { resolveBuildVersion } from '#~/analytics/utils/build-version.js';

// Signup-specific behavior lives here rather than changing capture/reset or
// attribution semantics for every consumer of the shared PostHog manager.
export const POSTHOG_RESET_MARKER_KEY = 'iris_posthog_reset_seen';
const LAST_KNOWN_USER_ID_KEY = 'analytics.last_known_user_id';

export const recordSignupBrowserReset = (): void => {
  try {
    window.localStorage.setItem(POSTHOG_RESET_MARKER_KEY, '1');
  } catch {
    // Tracking storage must never block logout.
  }
};

const wasPostHogReset = (): boolean => {
  try {
    return (
      window.localStorage.getItem(POSTHOG_RESET_MARKER_KEY) === '1' ||
      window.localStorage.getItem(LAST_KNOWN_USER_ID_KEY) !== null
    );
  } catch {
    return true;
  }
};

const readAttributionValue = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.length ? raw.slice(0, 256) : undefined;

/** Read the SDK's first-touch evidence only for the signup authorize handoff. */
export const readSignupAttribution = (): Record<string, string> => {
  try {
    const sdk = PostHogManager.getInstance().getPostHog();
    if (isElectronTrayWindow() || !sdk) return {};
    const ph = sdk as PostHog & {
      getSessionProperty?: (name: string) => unknown;
    };
    const attribution: Record<string, string> = {};
    const pickCampaign = (read: (name: string) => unknown): Record<string, string> => {
      const campaign: Record<string, string> = {};
      for (const name of ATTRIBUTION_PARAMS) {
        const raw = read(name);
        // Opaque click ids must remain intact; never manufacture an identifier
        // by clipping either SDK persistence or a parsed URL parameter.
        if (!name.startsWith('utm_') && typeof raw === 'string' && raw.length > 256) {
          // Observed-but-omitted is not absent: preserve that distinction through
          // packing and local recovery so identify cannot erase the SDK's full ID.
          campaign.ac = '0';
          campaign.ck ??= name;
          continue;
        }
        const value = readAttributionValue(raw);
        if (value) {
          campaign[name] = value;
        }
      }
      return campaign;
    };
    const hasCampaign = (campaign: Record<string, string>): boolean =>
      Object.keys(campaign).length > 0;

    // posthog-js persists the compact first-touch {u,r} object in its shared
    // cookie. Current-session campaign properties live on each origin and
    // cannot represent a marketing-site landing after a subdomain redirect.
    const initial = ph.get_property('$initial_person_info') as
      | { u?: unknown; r?: unknown }
      | undefined;
    // The SDK retains its previous persistence format instead of creating the
    // compact object for returning visitors. Read that format without mixing
    // their original campaign with a newer session's campaign.
    const legacyCampaign = ph.get_property('$initial_campaign_params') as
      | Record<string, unknown>
      | undefined;
    const legacyReferrer = ph.get_property('$initial_referrer_info') as
      | Record<string, unknown>
      | undefined;

    type AttributionSource = {
      campaign: Record<string, string>;
      url?: string | undefined;
      referrer?: string | undefined;
    };
    let firstTouch: AttributionSource | undefined;
    // posthog-js getPersonInfo truncates this cookie URL at 1,000 characters.
    // Treat a boundary-length snapshot as incomplete, including when a test or
    // older writer supplies more bytes; a trailing parameter is not a full ID.
    const initialUrl =
      typeof initial?.u === 'string' && initial.u.length <= 8192 ? initial.u : undefined;
    if (initialUrl) {
      try {
        const potentiallyTruncated = initialUrl.length >= 1000;
        const queryStart = initialUrl.indexOf('?');
        const cutQuery = potentiallyTruncated && queryStart >= 0 && !initialUrl.includes('#');
        const safeEnd = Math.max(queryStart, initialUrl.lastIndexOf('&'));
        const parsed = new URL(cutQuery ? initialUrl.slice(0, safeEnd) : initialUrl);
        const campaign = pickCampaign((name) => parsed.searchParams.get(name));
        if (potentiallyTruncated) {
          campaign.ac = '0';
          if (cutQuery) {
            const trailingKey = new URLSearchParams(initialUrl.slice(safeEnd + 1))
              .keys()
              .next().value;
            if (
              trailingKey &&
              ATTRIBUTION_PARAMS.some((key) => key === trailingKey) &&
              !trailingKey.startsWith('utm_') &&
              !campaign[trailingKey]
            )
              campaign.ck ??= trailingKey;
          }
        }
        // reset() on logout re-seeds first touch from an app route, so a bare
        // app-route record only counts (as a direct visit) in a browser that
        // was never reset. A campaign link straight to the app always counts.
        if (
          typeof window === 'undefined' ||
          parsed.host !== window.location.host ||
          hasCampaign(campaign) ||
          !wasPostHogReset()
        ) {
          firstTouch = { campaign, url: initialUrl, referrer: readAttributionValue(initial?.r) };
        }
      } catch {
        /* unknown provenance */
      }
    }
    if (!firstTouch && (legacyCampaign || legacyReferrer)) {
      // The legacy record has no URL, so it never supplies a landing_url.
      firstTouch = {
        campaign: pickCampaign((name) => legacyCampaign?.[name]),
        referrer: readAttributionValue(legacyReferrer?.$referrer)
      };
    }

    const readCurrentUrl = () =>
      readAttributionValue(
        ph.get_property('$current_url') ??
          ph.getSessionProperty?.('$current_url') ??
          (typeof window !== 'undefined' ? window.location.href : undefined)
      );
    const readCurrentReferrer = () =>
      readAttributionValue(
        ph.get_property('$referrer') ??
          ph.getSessionProperty?.('$referrer') ??
          (typeof document !== 'undefined' ? document.referrer : undefined)
      );

    // First-touch campaign wins. Without one, a campaign on this session or
    // the live URL is the best evidence; never mix fields across sources.
    let source: AttributionSource;
    if (firstTouch && hasCampaign(firstTouch.campaign)) {
      source = firstTouch;
    } else {
      const sessionCampaign = pickCampaign(
        (name) => ph.get_property(name) ?? ph.getSessionProperty?.(name)
      );
      const liveParams =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined;
      const campaign = hasCampaign(sessionCampaign)
        ? sessionCampaign
        : pickCampaign((name) => liveParams?.get(name));
      source =
        hasCampaign(campaign) || !firstTouch
          ? { campaign, url: readCurrentUrl(), referrer: readCurrentReferrer() }
          : firstTouch;
    }

    Object.assign(attribution, source.campaign);
    if (source.url) {
      attribution.landing_url = source.url;
    }
    if (source.referrer) {
      attribution.referrer = source.referrer;
    }

    // A missing referrer is not affirmative direct traffic. Only the SDK's
    // recorded first touch can distinguish direct from a lost/absent handoff.
    attribution.attribution_status = hasCampaign(source.campaign)
      ? 'preserved'
      : source === firstTouch && source.referrer === '$direct'
        ? 'direct'
        : source === firstTouch && source.referrer
          ? 'preserved'
          : 'unavailable';

    return attribution;
  } catch {
    // Optional campaign recovery cannot interrupt a login redirect.
    return {};
  }
};

/** Queue ONLY the signed signup identity merge; true is not delivery acknowledgement. */
export const captureSignupIdentity = async (
  properties: { distinct_id: string; $anon_distinct_id: string },
  options: CaptureOptions
): Promise<boolean> => {
  try {
    const sdk = PostHogManager.getInstance().getPostHog();
    if (isElectronTrayWindow() || !sdk) return false;
    const client = isElectronApp() ? ENVELOPE_CLIENT.ELECTRON : ENVELOPE_CLIENT.STANDALONE_WEB;
    const envelope: Record<string, string> = {
      [COMMON_PROPERTIES.CLIENT]: client,
      [COMMON_PROPERTIES.STUDIO_VERSION]: resolveBuildVersion(config.appVersion)
    };
    if (isElectronApp()) {
      // This merge is coalesced per boot, so it needs no second shared version
      // cache. Keep the manager's fallback behavior if IPC is unavailable.
      let version: string | undefined;
      try {
        const { ElectronAPI } = await import('#~/electron/api.js');
        version = await ElectronAPI.appVersion();
      } catch {
        /* use config fallback */
      }
      envelope[COMMON_PROPERTIES.ELECTRON_VERSION] = version || config.electronVersion || 'unknown';
    }
    // Direct SDK capture retains its opt-out, before_send and enrichment hooks.
    // It does not mutate the browser identity like identify() would.
    return sdk.capture('$identify', { ...properties, ...envelope }, options) !== undefined;
  } catch {
    return false;
  }
};
